import { config } from '../lib/config'
import { createOAuthClient, readJwtClaims } from './oauth-client'

/**
 * Google sign-in via the Cognito hosted UI. Identifies the user and gates
 * playlist storage; unrelated to the Spotify connection.
 */
export const authClient = createOAuthClient({
  authorizeEndpoint: `${config.cognitoDomain}/oauth2/authorize`,
  tokenEndpoint: `${config.cognitoDomain}/oauth2/token`,
  clientId: config.cognitoClientId,
  redirectUri: config.cognitoRedirectUri,
  scopes: ['openid', 'email', 'profile'],
  storageNamespace: 'songster.auth',
  // Google is the only identity provider in v1, so skip the chooser screen.
  extraAuthorizeParams: { identity_provider: 'Google' },
})

export interface UserProfile {
  userId: string
  email: string | null
  name: string | null
}

export function currentUser(): UserProfile | null {
  const tokens = authClient.tokens()
  if (!tokens?.idToken) return null
  const claims = readJwtClaims(tokens.idToken)
  if (!claims || typeof claims.sub !== 'string') return null
  return {
    userId: claims.sub,
    email: typeof claims.email === 'string' ? claims.email : null,
    name:
      typeof claims.name === 'string'
        ? claims.name
        : typeof claims.email === 'string'
          ? claims.email
          : null,
  }
}

export function hostedUiLogoutUrl(): string {
  const url = new URL(`${config.cognitoDomain}/logout`)
  url.searchParams.set('client_id', config.cognitoClientId)
  url.searchParams.set('logout_uri', new URL('/', window.location.origin).toString())
  return url.toString()
}

/**
 * The token sent to our API. The HTTP API's JWT authorizer is configured with
 * the app client as audience, which the ID token carries in `aud`.
 */
export async function getApiToken(): Promise<string> {
  await authClient.getAccessToken()
  const tokens = authClient.tokens()
  if (!tokens?.idToken) throw new Error('No ID token available')
  return tokens.idToken
}
